import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout/Layout';
import apiClient from '../services/apiClient';
import { goToExternal } from '../utils/navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faCheckCircle, faClock } from '@fortawesome/free-regular-svg-icons';
import Skeleton, { SkeletonText, SkeletonButton } from '../components/common/Skeleton/Skeleton';
import './Dashboard.scss';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalContacts: 0,
    totalTasks: 0,
    pendingTasks: 0,
    recentTasks: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const [contactsRes, tasksRes] = await Promise.all([
        apiClient.get('/api/contacts/'),
        apiClient.get('/api/tasks/')
      ]);

      const contacts = contactsRes.data.results || [];
      const tasks = tasksRes.data.results || [];
      
      const pendingTasks = tasks.filter(task => 
        task.status === null || task.status === 1
      ).length;

      const recentTasks = tasks.slice(0, 3);

      setStats({
        totalContacts: contactsRes.data.count || contacts.length,
        totalTasks: tasksRes.data.count || tasks.length,
        pendingTasks,
        recentTasks
      });
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  // navigation helper extracted to ui/src/utils/navigation.js

  if (loading) {
    return (
      <Layout>
        <div className="dashboard-container">
          <div className="container">
            <div className="dashboard-header">
              <div>
                <Skeleton width="250px" height="35px" borderRadius="8px" className="mb-8px" />
                <Skeleton width="400px" height="20px" borderRadius="6px" />
              </div>
              <SkeletonButton width="200px" height="48px" />
            </div>

            {/* Skeleton de estadísticas */}
            <div className="stats-grid">
              {[1, 2, 3].map(i => (
                <div key={i} className="stat-card">
                  <Skeleton width="60px" height="60px" borderRadius="12px" />
                  <div className="stat-content">
                    <Skeleton width="80px" height="40px" borderRadius="8px" className="mb-8px" />
                    <Skeleton width="120px" height="18px" borderRadius="6px" />
                  </div>
                </div>
              ))}
            </div>

            {/* Skeleton de tareas recientes */}
            <div className="recent-tasks-section">
              <div className="section-header">
                <Skeleton width="200px" height="30px" borderRadius="8px" />
                <SkeletonButton width="180px" height="38px" />
              </div>
              <div className="tasks-grid">
                {[1, 2, 3].map(i => (
                  <div key={i} className="task-card">
                    <div className="task-info">
                      <Skeleton width="90%" height="20px" borderRadius="6px" className="mb-12px" />
                      <SkeletonText lines={3} width={['100%', '95%', '70%']} height="14px" spacing="8px" />
                      <Skeleton width="120px" height="16px" borderRadius="6px" style={{ marginTop: '12px' }} />
                    </div>
                    <SkeletonButton width="100%" height="36px" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="dashboard-container">
        <div className="container">
          <div className="dashboard-header">
            <div>
              <h1>Dashboard</h1>
              <p>Bienvenido al sistema de gestión de tareas</p>
            </div>
            <div className="header-actions">
              <button 
                className="btn btn-secondary btn-afip-reports"
                onClick={() => navigate('/admin/questions/task/informes-vialidad')}
              >
                Informes de Vialidad
              </button>
              <button 
                className="btn btn-primary btn-create-task" 
                onClick={() => navigate('/admin/questions/task/incidentes')}
              >
                Crear nueva tarea
              </button>
             
            </div>
          </div>

          {/* Estadísticas */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faUser} />
              </div>
              <div className="stat-content">
                <h3>{stats.totalContacts}</h3>
                <p>Contactos</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <div className="stat-content">
                <h3>{stats.totalTasks}</h3>
                <p>Tareas Totales</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="stat-content">
                <h3>{stats.pendingTasks}</h3>
                <p>Tareas Pendientes</p>
              </div>
            </div>
          </div>

          {/* Tareas recientes */}
          {stats.recentTasks.length > 0 && (
            <div className="recent-tasks-section">
              <div className="section-header">
                <h2>Tareas recientes</h2>
                <button 
                  className="btn btn-secondary btn-view-all"
                  onClick={() => goToExternal('/admin/questions/task/')}
                >
                  Ver todas las tareas
                </button>
              </div>
              <div className="tasks-grid">
                {stats.recentTasks.map((task) => (
                  <div key={task.id} className="task-card">
                    <div className="task-info">
                      <h4>{task.title}</h4>
                      <span className="task-date">
                        {new Date(task.created_at).toLocaleDateString('es-AR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    <button 
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate(`/admin/questions/task/incidentes/${task.id}`)}
                    >
                      Ver
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats.recentTasks.length === 0 && (
            <div className="empty-state">
              <p>No hay tareas registradas</p>
              <button 
                className="btn btn-primary" 
                onClick={() => navigate('/admin/questions/task/incidentes')}
              >
                Crear Primera Tarea
              </button>
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
};

export default Dashboard;

